import { type Row } from "@tanstack/react-table";
import cx from "classnames";
import { type ChangeEventHandler, type FC, type KeyboardEvent, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { EditIcon } from "@moment/icons";
import { InputText, MomentButton } from "@moment/ui";

import { Popover } from "../../PopoverV2";
import { type DataGridColumnDefURL } from "../useColumnState";
import { type TableCellValueCommonProps } from "./types";

export type TableCellValueURLProps = TableCellValueCommonProps & {
	columnTypeInfo: DataGridColumnDefURL<unknown, unknown>["fieldTypeInfo"];
	row: Row<unknown>;
	columnID: string;
	updateData?: (rowIndex: number, columnId: string, value: unknown) => void;
};

const DEFAULT_TEXT_ACCESSOR_KEY_SUFFIX = "UrlText";
const DEBOUNCE_DELAY = 200;

export const TableCellValueURL: FC<TableCellValueURLProps> = ({
	value,
	defaultValue,
	onChange,
	isEditable,
	columnTypeInfo,
	row,
	columnID,
	updateData,
}) => {
	// Variables
	const textAccessorKey =
		columnTypeInfo?.textAccessorKey ?? `${columnID}${DEFAULT_TEXT_ACCESSOR_KEY_SUFFIX}`;
	const originalLabelValue =
		textAccessorKey !== undefined && typeof row.original === "object" && row.original !== null
			? row.original[textAccessorKey]
			: undefined;

	// Hooks
	const [open, setOpen] = useState(false);
	const [labelValue, setLabelValue] = useState(originalLabelValue);
	const [urlValue, setUrlValue] = useState(value ?? defaultValue);
	const [isUrlInvalid, setIsUrlInvalid] = useState(false);
	const openPopover = () => setOpen(true);
	const closePopover = () => {
		if (isUrlInvalid) {
			return;
		}
		setUrlValue(value ?? defaultValue);
		setLabelValue(originalLabelValue);

		setOpen(false);
	};

	// Functions
	const debouncedHandleURLChange = useDebouncedCallback(
		(value: string) => onChange?.(value),
		DEBOUNCE_DELAY
	);
	const debouncedHandleLabelChange = useDebouncedCallback((value: string) => {
		updateData?.(row.index, textAccessorKey, value);
	}, DEBOUNCE_DELAY);
	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		switch (e.key) {
			case "Enter":
			case "Escape":
				closePopover();
				break;
		}
	};
	const handleTextChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		const { value } = e.target;
		setLabelValue(value);
		debouncedHandleLabelChange(value);
	};
	const handleUrlChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		const { value } = e.target;
		setUrlValue(value);

		if (value === "") {
			setIsUrlInvalid(true);
			return;
		}
		setIsUrlInvalid(false);
		debouncedHandleURLChange(value);
	};

	const editButton = (
		<div className={cx("transition-opacity group-hover:opacity-100", !open && "opacity-0")}>
			<Popover.Root open={open}>
				<Popover.Trigger asChild>
					<MomentButton
						level="tertiary"
						onClick={openPopover}
						label="Edit URL"
						icon={EditIcon}
						size="sm"
					/>
				</Popover.Trigger>
				<Popover.Portal>
					<Popover.Content
						onInteractOutside={closePopover}
						onEscapeKeyDown={closePopover}
					>
						<div className="flex w-60 flex-col gap-4">
							<div className="flex flex-col gap-2">
								<label
									htmlFor="editUrlText"
									className="text-sm font-bold text-tertiary"
								>
									Text
								</label>
								<InputText
									size="sm"
									id="editUrlText"
									placeholder="Enter text"
									value={labelValue}
									onChange={handleTextChange}
									onKeyDown={handleKeyDown}
									disabled={textAccessorKey === undefined}
								/>
							</div>

							<div className="flex flex-col gap-2">
								<label
									htmlFor="editUrlHref"
									className="text-sm font-bold text-tertiary"
								>
									URL
								</label>
								<InputText
									size="sm"
									id="editUrlHref"
									placeholder="Enter a URL"
									variant={isUrlInvalid ? "negative" : "default"}
									value={urlValue}
									onChange={handleUrlChange}
									onKeyDown={handleKeyDown}
								/>
								{isUrlInvalid && (
									<div className="text-xs text-negative">URL cannot be empty</div>
								)}
							</div>
						</div>
					</Popover.Content>
				</Popover.Portal>
			</Popover.Root>
		</div>
	);

	return (
		<div className="group flex w-full items-center gap-2 pr-1">
			<div className="flex-1 break-all">
				<a href={value} target="_blank" rel="noreferrer">
					{originalLabelValue || value}
				</a>
			</div>
			<div className="shrink-0 grow-0">{isEditable && editButton}</div>
		</div>
	);
};
